import { useQuery } from '@tanstack/react-query';

import { fetchEntitlements } from '@api-service/entitlements';
import type { MembershipEntitlements } from '@api-service/entitlements';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0SessionUser } from '@hooks/useAuth0SessionUser';

export const ENTITLEMENTS_QUERY_KEY = ['users-me-entitlements'] as const;

// Long enough that navigating around the site does not re-ask on every page,
// short enough that a plan bought in another tab is picked up without a
// reload. A checkout return does not wait for it: the thank-you page
// invalidates this query as soon as the entitlement lands.
const ENTITLEMENTS_STALE_TIME_IN_MS = 5 * 60 * 1000;

const ENTITLEMENTS_RETRY_ATTEMPTS = 2;

/**
 * The signed-in member's tier, roles and subscription, straight from
 * ffh-backend (ADR-005). This is the source the role helpers read through
 * useAuth0User; components should reach for that rather than this hook.
 *
 * Resolves to undefined for anonymous visitors, and on failure, which leaves
 * every caller on the claims the Auth0 session already carries.
 */
export const useEntitlements = (): {
  entitlements: MembershipEntitlements | undefined;
  isEntitlementsLoading: boolean;
} => {
  const { user } = useAuth0SessionUser();
  const { accessToken } = useAccessToken();

  const { data, isLoading } = useQuery({
    queryKey: [...ENTITLEMENTS_QUERY_KEY, user?.sub],
    queryFn: () => fetchEntitlements(accessToken as string),
    enabled: Boolean(user) && Boolean(accessToken),
    staleTime: ENTITLEMENTS_STALE_TIME_IN_MS,
    // A read that fails leaves every role helper on the claims the Auth0
    // session carries, which is now as old as the login rather than at most a
    // few days. Retrying, and revalidating when the member comes back to the
    // tab, is what stops one bad response holding them there for the life of
    // the page.
    retry: ENTITLEMENTS_RETRY_ATTEMPTS
  });

  return {
    entitlements: data,
    isEntitlementsLoading: isLoading && Boolean(user) && Boolean(accessToken)
  };
};
